import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { apiClient } from '../utils/apiClient'
import { isOrganizationPath } from '../utils/organizationSettingsNav'
import { rememberLastProjectId } from '../utils/databaseProjectExit'
import {
  environmentContextForDeployment,
  environmentSidebarExitPath,
  isEnvironmentSidebarPath,
} from '../utils/environmentSettingsNav'

export type ResourceSidebarContext =
  | { mode: 'global' }
  | {
      mode: 'organization'
      exitPath: '/projects'
    }
  | {
      mode: 'project'
      projectId: string
      envId: string | null
      deploymentId: string | null
      exitPath: '/projects'
    }
  | {
      mode: 'environment'
      projectId: string
      envId: string
      exitPath: string
    }
  | {
      mode: 'database'
      databaseId: string
      exitPath: '/databases'
    }

function parsePathContext(pathname: string): ResourceSidebarContext {
  if (isOrganizationPath(pathname)) {
    return {
      mode: 'organization',
      exitPath: '/projects',
    }
  }

  const projectMatch = pathname.match(/^\/project\/(\d+)/)
  if (projectMatch) {
    const projectId = projectMatch[1]
    const envMatch = pathname.match(/\/environment\/(\d+)/)
    const envId = envMatch ? envMatch[1] : null
    if (envId && isEnvironmentSidebarPath(pathname, projectId, envId)) {
      return {
        mode: 'environment',
        projectId,
        envId,
        exitPath: environmentSidebarExitPath(projectId),
      }
    }
    return {
      mode: 'project',
      projectId,
      envId,
      deploymentId: null,
      exitPath: '/projects',
    }
  }

  const deploymentMatch = pathname.match(/^\/deployment\/(\d+)/)
  if (deploymentMatch) {
    return {
      mode: 'project',
      projectId: '',
      envId: null,
      deploymentId: deploymentMatch[1],
      exitPath: '/projects',
    }
  }

  const syntheticMatch = pathname.match(/^\/synthetic-databases\/(\d+)/)
  if (syntheticMatch) {
    return {
      mode: 'database',
      databaseId: syntheticMatch[1],
      exitPath: '/synthetic-databases',
    }
  }

  const databaseMatch = pathname.match(/^\/databases\/(\d+)/)
  if (databaseMatch) {
    return {
      mode: 'database',
      databaseId: databaseMatch[1],
      exitPath: '/databases',
    }
  }

  return { mode: 'global' }
}

export function useResourceSidebarContext(): ResourceSidebarContext & { isContextMode: boolean } {
  const { pathname, search } = useLocation()
  const base = useMemo(() => parsePathContext(pathname), [pathname])
  const [deploymentProjectId, setDeploymentProjectId] = useState<string | null>(null)
  const [deploymentEnvId, setDeploymentEnvId] = useState<string | null>(null)

  const deploymentId =
    base.mode === 'project' && base.deploymentId ? base.deploymentId : null

  useEffect(() => {
    if (!deploymentId) {
      setDeploymentProjectId(null)
      setDeploymentEnvId(null)
      return
    }
    let cancelled = false
    apiClient
      .get<{ environment?: { id?: number; project_id?: number } }>(`/api/deployments/${deploymentId}`)
      .then((data) => {
        if (cancelled) return
        const pid = data?.environment?.project_id
        const eid = data?.environment?.id
        setDeploymentProjectId(pid != null ? String(pid) : null)
        setDeploymentEnvId(eid != null ? String(eid) : null)
      })
      .catch(() => {
        if (!cancelled) {
          setDeploymentProjectId(null)
          setDeploymentEnvId(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [deploymentId])

  const resolved = useMemo((): ResourceSidebarContext => {
    if (base.mode !== 'project' || !base.deploymentId) return base
    const envCtx = environmentContextForDeployment(deploymentProjectId, deploymentEnvId)
    if (envCtx) {
      return { mode: 'environment', ...envCtx }
    }
    return {
      ...base,
      projectId: deploymentProjectId ?? '',
      envId: deploymentEnvId,
    }
  }, [base, deploymentProjectId, deploymentEnvId])

  useEffect(() => {
    if (
      (resolved.mode === 'project' || resolved.mode === 'environment') &&
      resolved.projectId
    ) {
      rememberLastProjectId(resolved.projectId)
      return
    }
    const fromQuery = new URLSearchParams(search).get('projectId')
    if (fromQuery) rememberLastProjectId(fromQuery)
  }, [resolved, search])

  return {
    ...resolved,
    isContextMode: resolved.mode !== 'global',
  }
}
